import React, { useEffect } from "react";
import { connect } from "react-redux";
import { Button, Tab, Tabs, Tooltip } from "carbon-components-react";
import { Renew16 } from "@carbon/icons-react";

import APIClient from "web_modules/APIClient";
import HLHLContacts from "web_modules/HLHLContacts";
import MCT from "web_modules/mct";

import HLHLContactsProvider from "web_modules/HLHLContacts/HLHLContactsProvider";
import LoggedInContainer from "web_modules/shared/LoggedInContainer";
import { Role } from "@eave/shared/types/api";
import { finishLoading as finishLoadingAction } from "underwriter_dashboard/actions";
import { getSessionToken } from "@eave/borrower/web_modules/shared/session";
import { tokenHasRoles } from "web_modules/shared/session";

import AdminTaskConfig from "./TaskConfig";
import DownloadIncidentsButton from "./PagerDutyIncidents";
import { getHeaderLinksForUser, navPaths } from "../shared/nav_paths";
import FinancialDataBulkTool from "./FinancialDataBulkTool";

const styles = require("./styles.module.scss");

interface TabDescriptor {
  id: string;
  label: string;
  url: string;
  children?: any;
  tabContent: any;
}

const tasksTab: TabDescriptor = {
  id: "tasks-tab",
  label: "Tasks",
  url: `${navPaths.tools}/task_admin`,
  children: (
    <div className={styles.tab}>
      <div className={styles.title}>
        <h2>Tasks</h2>
        <Tooltip>
          This is a tool to configure the tasks that are automatically added to a loan, with
          editable params for task visibility, task completion, and due dates.
        </Tooltip>
      </div>
      <AdminTaskConfig />
    </div>
  ),
  tabContent: (
    <div className={styles.tab}>
      <div className={styles.title}>
        <h2>Tasks</h2>
        <Tooltip>
          This is a tool to configure the tasks that are automatically added to a loan, with
          editable params for task visibility, task completion, and due dates.
        </Tooltip>
      </div>
      <AdminTaskConfig />
    </div>
  ),
};

const refreshSlackTab: TabDescriptor = {
  id: "refresh-slack-tab",
  label: "Refresh Slack",
  url: `${navPaths.tools}/refresh_slack`,
  tabContent: (
    <div className={styles.tab}>
      <p className={styles.desc}>
        Click this button if Slack notifications are not working. It will reload the list of public
        channels, private channels, and users.
      </p>
      <Tooltip triggerText="What is this?" direction="right">
        If operators archive or unarchive Slack channels via Slack instead of via TRUS, things get
        out of sync, and TRUS doesn't see that change. Because of historical engineering changes,
        the fix for this requires a button.
        <br />
        <br />
        If nobody manually unarchives Slack channels, things are generally fine. But if someone
        unarchives a Slack channel manually, it won't get messages from TRUS until you hit the
        button.
      </Tooltip>
      <Button
        className={styles.button}
        renderIcon={Renew16}
        onClick={() => APIClient.post("/uw/slack/refresh", {})}
      >
        Refresh Slack
      </Button>
    </div>
  ),
};

const pagerDutyIncidentsTab: TabDescriptor = {
  id: "pager-duty-incidents-tab",
  label: "PagerDuty incidents",
  url: `${navPaths.tools}/pagerduty_incidents`,
  tabContent: (
    <div className={styles.tab}>
      <p className={styles.desc}>Download the latest PagerDuty incidents as a CSV file.</p>
      <DownloadIncidentsButton />
    </div>
  ),
};

const csvUploadTab: TabDescriptor = {
  id: "financial-reporting-tab",
  label: "Financial Reporting",
  url: `${navPaths.tools}/financial_reporting`,
  tabContent: (
    <div className={styles.tab}>
      <FinancialDataBulkTool />
    </div>
  ),
};

const hlhlContactsTab: TabDescriptor = {
  id: "hlhl-contacts-tab",
  label: "HLHL contacts",
  url: `${navPaths.tools}/hlhl_contacts`,
  tabContent: (
    <div className={styles.tab}>
      <div className={styles.title}>
        <h2>HLHL contacts</h2>
        <Tooltip>
          This is a tool to maintain HLHL contacts, including LOs, LMs, underwriters, and settlement
          agents. You can add new HLHL contacts, toggle whether existing contacts are active, and
          edit contact details.
        </Tooltip>
      </div>
      <HLHLContactsProvider>
        <HLHLContacts />
      </HLHLContactsProvider>
    </div>
  ),
};

const mctReportsTab: TabDescriptor = {
  id: "mct-reports-tab",
  label: "MCT Reports",
  url: `${navPaths.tools}/mct_reports`,
  tabContent: (
    <div className={styles.tab}>
      <MCT />
    </div>
  ),
};

const getTabDescriptionsForUser = (): TabDescriptor[] => {
  const token = getSessionToken();
  if (
    tokenHasRoles(
      token,
      Role.Admin,
      Role.OpsAdmin,
      Role.CapitalMarkets,
      Role.LoanOfficer,
      Role.Underwriter,
    )
  ) {
    return [
      tasksTab,
      refreshSlackTab,
      pagerDutyIncidentsTab,
      hlhlContactsTab,
      mctReportsTab,
      csvUploadTab,
    ];
  }
  if (tokenHasRoles(token, Role.CashCloseAdmin)) {
    return [hlhlContactsTab];
  }
  return [];
};

interface Props {
  history: any;
  location: any;
  finishLoading: () => (dispatch: any) => any;
}

const ToolsDashboard: React.FC<Props> = ({ history, location, finishLoading }) => {
  useEffect(() => {
    finishLoading();
  }, [finishLoading]);

  const tabDescriptors = getTabDescriptionsForUser();

  // if someone hits the base url (without a tab), automatically go to the first tab instead
  useEffect(() => {
    if (location.pathname === navPaths.tools && tabDescriptors.length) {
      history.push(tabDescriptors[0].url);
    }
  }, [location.pathname]);

  const activeTab = tabDescriptors.findIndex(d => location.pathname.includes(d.url)) || 0;
  const getTabProps = (d: TabDescriptor) => {
    return {
      id: d.id,
      label: d.label,
      url: d.url,
      children: d.children,
      selected: location.pathname.includes(d.url),
      onClick: () => history.push(d.url),
    };
  };

  return (
    <div className={styles.container}>
      <LoggedInContainer
        titleLink={{
          name: "Tools",
          to: navPaths.tools,
        }}
        headerLinks={getHeaderLinksForUser(getSessionToken())}
      >
        <Tabs selected={activeTab}>
          {tabDescriptors.map(d => (
            <Tab key={d.id} {...getTabProps(d)}>
              {d.tabContent}
            </Tab>
          ))}
        </Tabs>
      </LoggedInContainer>
    </div>
  );
};

export default connect(null, { finishLoading: finishLoadingAction })(ToolsDashboard);
